import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface CampaignsState {
	creationDate: string | undefined
	modificationDate: string | undefined
	createdBy: string | undefined
	modifiedBy: string | undefined
	version: number
	id: number
	idLogo: string | undefined
	campaignName: string | undefined
	campaignLogo: string | undefined
	enabled: boolean
	deleted: boolean
	authorizationType: string | undefined
}

export interface PageableElement {
	content: CampaignsState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: CampaignsState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface ICreateResponse {}

interface IPatchPayload {
	idCampaign: number
	idBank: string
	campaignData: CampaignsState
}

interface CampaignsListResponse {
	nextUrl: null | string
	page: PageElement
}

interface ApprovalRulesListResponse {
	data: string[]
}

export interface CampaignsPaginatedListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: CampaignsListResponse
}

export interface AllCampaignsListState {
	loading: boolean
	error: undefined | AxiosError<string>
	list: CampaignsListResponse
}

export interface CampaignsPayload {
	filter?: any | undefined
	sort?: any | undefined
	page?: number | undefined
	pageSize?: number | undefined
}

export interface FetchCampaignsPayload {
	apiName: string
	url: string
}

interface ApprovalRulesPayload {
	campaignIdLogo: string
}

let cancelTokenFetch = null

export const getApprovalRules = createAsyncThunk<
	ApprovalRulesListResponse,
	ApprovalRulesPayload,
	{ rejectValue: string }
>(
	"campaigns/getApprovalRules",
	async (
		{ campaignIdLogo }: ApprovalRulesPayload,
		{ rejectWithValue, _dispatch }
	) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicampaign/v2/getApproveRules`

			const response: AxiosResponse<ApprovalRulesListResponse> =
				await axios.post(baseUrl, { campaignIdLogo: campaignIdLogo }, config)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<ApprovalRulesListResponse>
			return rejectWithValue(axiosError?.response?.data ?? "error.generic")
		}
	}
)

export const getCampaignsPaginated = createAsyncThunk<
	CampaignsListResponse,
	CampaignsPayload,
	{ rejectValue: string }
>(
	"campaigns/getCampaignsPaginated",
	async (
		{ page, pageSize, filter, sort }: CampaignsPayload,
		{ rejectWithValue, dispatch }
	) => {
		try {
			if (cancelTokenFetch) {
				cancelTokenFetch.cancel("Operazione cancellata")
				cancelTokenFetch = null
			}

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicampaign/v2/getCampaignsListPaginated`
			const parameters = new URLSearchParams()

			if (page === 0 || page) {
				parameters.append("page", page.toString())
			}
			if (pageSize) {
				parameters.append("size", pageSize.toString())
			}
			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<CampaignsListResponse> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<CampaignsListResponse>
			return rejectWithValue(axiosError?.response?.data ?? "error.generic")
		}
	}
)

export const fetchCampaigns = createAsyncThunk<
	CampaignsListResponse,
	FetchCampaignsPayload,
	{ rejectValue: string }
>(
	"campaigns/fetchCampaigns",
	async ({ url }: FetchCampaignsPayload, { dispatch, rejectWithValue }) => {
		try {
			cancelTokenFetch = axios.CancelToken.source()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				cancelToken: cancelTokenFetch.token,
				withCredentials: true,
			}
			const response: AxiosResponse<CampaignsListResponse> = await axios.get(
				url,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<CampaignsListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const updateCampaign = createAsyncThunk<
	ICreateResponse,
	IPatchPayload,
	{ rejectValue: AxiosError }
>(
	"campaign/patch",
	async (
		{ campaignData, idBank, idCampaign }: IPatchPayload,
		{ rejectWithValue }
	) => {
		try {
			const formData = new FormData()
			Object.keys(campaignData).forEach((key) => {
				formData.append(key, campaignData[key])
			})

			/*
		if (bankData.bankImage) {
			formData.append('bankImage', bankData.bankImage);
		}*/
			const headerConfig: AxiosRequestConfig = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				withCredentials: true,
			}
			const response: AxiosResponse = await axios.patch(
				`${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apicampaign/v2/update/${idBank}/${idCampaign}`,
				formData,
				headerConfig
			)
			return response
		} catch (error) {
			return rejectWithValue(error?.response)
		}
	}
)
